import React from "react";
import SearchField from "../../common/SearchField";
import GenericDropDownList from "../../common/GenericDropDownList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faCalendarDays } from "@fortawesome/pro-solid-svg-icons";

interface JourneyListingSearchAndFilterProps {
  onSearch: (searchValue: string | null) => void;
  isLoading: boolean;
  loaderColor: string;
  hasUpcomingJourneys: boolean | null;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  searchTerm: string | null;
  selectedDateRange: string;
  setSelectedDateRange: (dateRange: string) => void;
}

const JourneyListingSearchAndFilter: React.FC<
  JourneyListingSearchAndFilterProps
> = ({
  onSearch,
  isLoading,
  loaderColor,
  hasUpcomingJourneys,
  selectedStatus,
  setSelectedStatus,
  searchTerm,
  selectedDateRange,
  setSelectedDateRange,
}) => {
  let statusOptions = [
    { key: "All Statuses", value: "All Statuses" },
    { key: "NOT-STARTED", value: "Not Started" },
    { key: "IN-PROGRESS", value: "In Progress" },
    { key: "COMPLETED", value: "Completed" },
  ];

  // If 'hasUpcomingJourney's is true then add 'UPCOMING' to the list of options
  if (hasUpcomingJourneys) {
    statusOptions.push({ key: "UPCOMING", value: "Upcoming" });
  }

  // Date range options
  const dateRangeOptions = [
    { key: "LAST_SIX_MONTHS", value: "Last six months" },
    { key: "LAST_YEAR", value: "In the last year" },
    { key: "ALL_TIME", value: "All time" },
  ];

  // Get the correct key for the dropdown based on display value
  const getKeyFromDisplayValue = (displayValue: string): string => {
    const option = statusOptions.find((opt) => opt.value === displayValue);
    return option ? option.key : displayValue;
  };

  // Get the display value from the key
  const getDisplayValueFromKey = (key: string): string => {
    const option = statusOptions.find((opt) => opt.key === key);
    return option ? option.value : key;
  };

  // Date range dropdown helpers
  const getDateRangeKeyFromDisplayValue = (displayValue: string): string => {
    const option = dateRangeOptions.find((opt) => opt.value === displayValue);
    return option ? option.key : displayValue;
  };

  const getDateRangeDisplayValueFromKey = (key: string): string => {
    const option = dateRangeOptions.find((opt) => opt.key === key);
    return option ? option.value : key;
  };

  // Determine the current value for the dropdowns
  const statusDropdownValue =
    selectedStatus === "All Statuses"
      ? "All Statuses"
      : getKeyFromDisplayValue(selectedStatus);

  const dateRangeDropdownValue =
    getDateRangeKeyFromDisplayValue(selectedDateRange);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full p-4 border border-gray-200 rounded-lg mb-4 gap-4 bg-white">
      {/* Search Field */}
      <SearchField
        onSearch={onSearch}
        isLoading={isLoading}
        loaderColor={loaderColor}
        searchTerm={searchTerm}
        className="flex-grow w-full sm:max-w-md"
        includeSetWidth={false}
        inputFieldClassName="text-sm px-3 py-2 rounded-md border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all placeholder-gray-400"
        placeholderText="Search by name for a journey or team member..."
        outerContainerClassName="w-full sm:mr-4"
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
        {/* Status Filter */}
        <div className="w-full sm:w-auto">
          <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
            <FontAwesomeIcon icon={faFilter} className="mr-1.5 text-gray-500" />
            Status
          </label>
          <GenericDropDownList
            inputId="status-filter"
            items={statusOptions}
            currentValue={statusDropdownValue}
            onChange={(value) => {
              const displayValue = getDisplayValueFromKey(
                value || "All Statuses"
              );
              setSelectedStatus(displayValue);
            }}
            className="w-full sm:w-48 px-3 py-2 text-sm border-gray-200 rounded-md hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
            applyBorder={true}
            bgColorClassName="bg-white"
            includeSelectOption={false}
          />
        </div>

        {/* Date Range Filter */}
        <div className="w-full sm:w-auto">
          <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
            <FontAwesomeIcon
              icon={faCalendarDays}
              className="mr-1.5 text-gray-500"
            />
            Date Range
          </label>
          <GenericDropDownList
            inputId="date-range-filter"
            items={dateRangeOptions}
            currentValue={dateRangeDropdownValue}
            onChange={(value) => {
              const displayValue = getDateRangeDisplayValueFromKey(
                value || "LAST_YEAR"
              );
              setSelectedDateRange(displayValue);
            }}
            className="w-full sm:w-48 px-3 py-2 text-sm border-gray-200 rounded-md hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
            applyBorder={true}
            bgColorClassName="bg-white"
            includeSelectOption={false}
          />
        </div>
      </div>
    </div>
  );
};

export default JourneyListingSearchAndFilter;
